<template>
  <section class="add-patients-with-operations-form">
    <Breadcrumbs :items="breadcrumbs">
      <div class="consent-link">
        <router-link :to="{name: 'patient-consent'}">
          <rp-sprite class="consent-icon" name="info"/>
          View informed consent
        </router-link>
      </div>
    </Breadcrumbs>
    <div class="content">
      <rp-patients-with-operations
        v-model="model"
        :iols="iols"
        :trials="trials"
        :locales="locales"
        :hospitals="hospitals"
        :defaultHospitalId="defaultHospitalId"
        :questionnaires="questionnaires"
        :operationTypes="operationTypes"
        :defaultQuestionnaires="surgeonQuestionnaires"
        :surgeons="surgeons"
        :templateUrl="templateUrl"
        :advancedTemplateUrl="advancedTemplateUrl"
        :learnUrl="learnUrl"
        :advancedLearnUrl="advancedLearnUrl"
        @surgeonChanged="onSurgeonChanged"
        @submitOperations="onSubmitOperations"
        @submitCsv="onSubmitCsv"
      />
    </div>
    <rp-overlay :active="showConfirmDialog">
      <rp-confirm
        icon="warning_rounded"
        messageText='I confirm that I have read the consent statement to ALL the patients within this form and received their oral consent'
        :confirm="submitOperations"
        :cancel="closeConfirmDialog"
      />
    </rp-overlay>
    <rp-overlay :active="showCsvConfirmDialog">
      <rp-confirm
        icon="warning_rounded"
        messageText='I confirm that I have read the consent statement to ALL the patients within this file and received their oral consent'
        :confirm="submitCsv"
        :cancel="closeConfirmDialog"
      />
    </rp-overlay>
    <rp-overlay :active="showSuccessDialog">
      <rp-alert
        icon="raypro_icon"
        message="Operation(s) uploaded succesfully!"
        :onClick="closeSuccessDialog"
      />
    </rp-overlay>
    <rp-overlay :active="showErrorDialog">
      <rp-alert
        class="error-icon"
        icon="warning_rounded"
        :message="errorMessage"
        :onClick="closeErrorDialog"
      />
    </rp-overlay>
  </section>
</template>

<script>
  import page from '@/mixins/page'
  import form from 'raypro_ui/src/mixins/form'
  import Breadcrumbs from '@/components/Breadcrumbs'
  import Alert from 'raypro_ui/src/components/ui/Alert'
  import Overlay from 'raypro_ui/src/components/ui/Overlay'
  import Sprite from 'raypro_ui/src/components/ui/Sprite'
  import Confirm from 'raypro_ui/src/components/ui/DeleteConfirm'
  import PatientsWithOperations from 'raypro_ui/src/components/ui/forms/PatientsWithOperations'
  import { getUserHospitals, getDefaultHospital, getAssociatedSurgeons, getPatientByEmail } from 'raypro_ui/src/api/users'
  import { getIolModels } from 'raypro_ui/src/api/iols'
  import { getLocales } from 'raypro_ui/src/api/locales'
  import { getOpenTrialsForSurgeon, createTrialPatient } from 'raypro_ui/src/api/trials'
  import { getQuestionnaires, getQuestionnairesForSurgeon, getValidQuestionnairesForSurgeon } from 'raypro_ui/src/api/questionnaire'
  import { createPatientsWithOperations, uploadOperationsCsv, nurseUploadOperationsCsv, getOperationTypes } from 'raypro_ui/src/api/operations'
  import { mapGetters } from 'vuex'

  const TEMPLATES_ROOT = runtimeconfig.TEMPLATES_ROOT || process.env.TEMPLATES_ROOT

  export default {
    components: {
      Breadcrumbs,
      'rp-alert': Alert,
      'rp-overlay': Overlay,
      'rp-sprite': Sprite,
      'rp-confirm': Confirm,
      'rp-patients-with-operations': PatientsWithOperations,
    },

    mixins: [page, form],

    data() {
      return {
        model: {
          csv: {file: '', hospital_id: '', locale_id: ''},
          accept_terms: false,
          accept_terms_csv: false,
          operations: [{
            id: 1,
            patient_email: '',
            locale_id: '',
            surgeon_id: '',
            nurse_id: '',
            hospital_id: '',
            left_iol_id: '',
            right_iol_id: '',
            questionnaire_ids: [],
            trial_id: '',
            subject_id: '',
            operation_type_id: '',
            do_trial_enrolment: false,
          }]
        },
        iols: [],
        trials: [],
        locales: [],
        surgeons: [],
        hospitals: [],
        questionnaires: [],
        surgeonQuestionnaires: [],
        operationTypes: [],
        defaultHospitalId: '',
        showConfirmDialog: false,
        showCsvConfirmDialog: false,
        showSuccessDialog: false,
        showErrorDialog: false,
        errorMessage: '',
        title: 'Add Patients & Surgeries',
        breadcrumbs: [{ label: 'Add Patients & Surgeries' }],
      }
    },

    computed: {
      ...mapGetters(['authUser', 'isSurgeon']),

      templateUrl() {
        return (this.isSurgeon) ? TEMPLATES_ROOT + "templ_op_sur.xlsm" : TEMPLATES_ROOT + "templ_op_nur.xlsm"
      },

      advancedTemplateUrl() {
        return (this.isSurgeon) ? TEMPLATES_ROOT + "templ_op_sur_adv.xlsm" : ""
      },

      learnUrl() {
        return (this.isSurgeon) ? 
          'https://www.youtube.com/watch?v=U15GdCI7DEs&list=PLhKnUX7I7jWrzPrauqGRYWvGcCQ3dMhni&index=5' :
          'https://www.youtube.com/watch?v=M1rcLB3e7wk&list=PLhKnUX7I7jWrzPrauqGRYWvGcCQ3dMhni&index=4'
      },

      advancedLearnUrl() {
        return (this.isSurgeon) ? 
          'https://www.youtube.com/watch?v=qR8N-jOQLM8&list=PLhKnUX7I7jWrzPrauqGRYWvGcCQ3dMhni&index=6' :
          ''
      },
    },

    mounted() {
      this.getIols()
      this.getLocales()
      this.getSurgeons()
      this.getHospitals()
      this.getQuestionnaires()
      this.getOperationTypes()

      if (this.$route.params.patientEmail) {
        this.model.operations[0].patient_email = this.$route.params.patientEmail
      }
    },

    methods: {
      getIols() {
        getIolModels().then(({data}) => {
          this.iols = data
        })
      },

      getLocales() {
        getLocales().then(({data}) => {
          this.locales = data;
        })
      },

      getSurgeons() {
        if (this.isSurgeon) {
          this.surgeons = [this.authUser]
        } else {
          getAssociatedSurgeons({userId: this.authUser.id}).then(({data}) => {
            this.surgeons = data
          })
        }
      },

      getHospitals(userId) {
        userId = (userId) ? userId : this.authUser.id
        getUserHospitals({id: userId}).then(({data}) => {
          this.hospitals = data
        })
        getDefaultHospital({userId: userId}).then(({data}) => {
          this.defaultHospitalId = (data) ? data.id : ''
        })
      },

      getQuestionnaires() {
        getQuestionnaires().then(({data}) => {
          this.questionnaires = data
        })
      },

      getOperationTypes() {
        getOperationTypes().then(({data}) => {
          this.operationTypes = data
        })
      },

      getQuestionnairesForSurgeon(surgeonId) {
        if (surgeonId) {
          getValidQuestionnairesForSurgeon(surgeonId).then(({data}) => {
            this.questionnaires = data
          })
          getQuestionnairesForSurgeon(surgeonId).then(({data}) => {
            this.surgeonQuestionnaires = data
          })
        } else {
          this.getQuestionnaires()
          this.surgeonQuestionnaires = []
        }
      },

      getTrialsForSurgeon(surgeonId) {
        if (surgeonId) {
          getOpenTrialsForSurgeon(surgeonId).then(({data}) => {
            this.trials = data
          })
        } else {
          this.trials = []
        }
      },

      onSurgeonChanged(surgeonId) {
        this.getHospitals(surgeonId)
        this.getQuestionnairesForSurgeon(surgeonId)
        this.getTrialsForSurgeon(surgeonId)
      },

      onSubmitOperations(operations) {
        this.showConfirmDialog = true
      },

      async submitOperations() {
        this.showConfirmDialog = false
        const operations = this.model.operations.map((operation) => {
          let op = JSON.parse(JSON.stringify(operation))
          op.performed_at = operation.performed_at + 'T12:00:00Z'
          return op
        })

        await createPatientsWithOperations({operations: operations})

        operations.forEach(async x => {
          if (x.do_trial_enrolment) {
            let {data} = await getPatientByEmail(x.patient_email)
            let patient = data[0]
            await createTrialPatient(x.trial_id, {trial_patient: {patient_id: patient.id, subject_id: x.subject_id}})
          }
        })

        this.showSuccessDialog = true
      },

      onSubmitCsv(csv) {
        this.showCsvConfirmDialog = true
      },

      submitCsv() {
        this.showCsvConfirmDialog = false
        const csv = this.model.csv
        const formData = new FormData()
        formData.append('file', csv.file)
        formData.append('hospital_id', csv.hospital_id)
        formData.append('locale_id', csv.locale_id)
        formData.append('operation_type_id', csv.operation_type_id)

        const uploadFunction = (this.isSurgeon) ? uploadOperationsCsv : nurseUploadOperationsCsv
        uploadFunction(formData).then(({data}) => {
          this.processCsvResult(data)
        })
      },

      processCsvResult(result) {
        if (result.message) {
          this.showSuccessDialog = true
        } else if (result.errors) {
          this.errorMessage = "The following errors occurred:<br/>"
          result.errors.forEach((e, i) => this.errorMessage += `${i+1}. ${e}<br/>`);
          this.showErrorDialog = true
        }
      },

      closeConfirmDialog() {
        this.showConfirmDialog = false
        this.showCsvConfirmDialog = false
      },

      closeSuccessDialog() {
        this.showSuccessDialog = false
        this.$router.push({name: 'all-patients'})
      },

      closeErrorDialog() {
        this.showErrorDialog = false
      },
    },

    validations: {
      resource: {
      },
    },
  }
</script>

<style lang="sass" scoped>
  @import 'raypro_ui/src/styles/base/_variables.sass'
  @import 'raypro_ui/src/styles/helpers/_mixins.sass'

  .add-patients-with-operations-form
    .consent-link
      font-size: larger
      .consent-icon
        width: 20px
        height: 20px
        margin-top: -3px
        fill: $color-text-invert
        vertical-align: middle

</style>



// WEBPACK FOOTER //
// src/components/pages/Patients/AddPatientsWithOperations.vue